/* eslint-disable no-nested-ternary */
import React, {useCallback, useEffect, useMemo, useRef} from 'react'
import {Controller, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {Checkbox, IconButton, Radio, RadioGroup} from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import Divider from '@material-ui/core/Divider'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Tab from '@material-ui/core/Tab'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {SchemaOf} from 'yup'
import {Features, isOrganizationHealth} from 'Authority'
import {useBoldInputStyle} from 'components/common/useBoldInputStyle'
import useStyles from 'components/Dialog/User/Style'
import {TabPanel, Tabs} from 'components/Tab'
import {
  Gender,
  GenderSelectItems,
  Hand,
  HandSelectItems,
  REGEX_PHONE,
} from 'constants/CommonConstant'
import * as storage from 'api/storageApi'
import {
  createPatientChartNoApi,
  doubleCheckPatientChartNoApi,
  PatientAddForm,
} from 'api/patientApi'
import useAuth from 'features/auth/useAuth'
import useNoticeConfirm from 'features/modal/useNoticeConfirm'
import useOrgUserList from 'features/org/useOrgUserList'
import {useCreateDialog} from 'features/patient/usePatientDialog'
import useProductList from 'features/patient/useProductList'
import useProductManagement from 'features/productManagement/useProductManagement'
import {EEG_INDICATOR_ID, HRV_INDICATOR_ID} from 'pages/ProductsPages/constant'
import {NORM_PACKAGE_ID} from 'pages/ProductOrderPages/constants'
import usePatientSearch from 'features/patient/usePatientSearch'
import {isNotDefined} from 'helpers/commonHelper'
import {dateToDashString} from 'helpers/dateHelper'
import SelectBoldCountryNumber from '../BoldSelect/SelectBoldCountryNumber'
import SelectBoldDoctor from '../BoldSelect/SelectBoldDoctor'
import SelectBoldYear from '../BoldSelect/SelectBoldYear'
import CheckUpItems, {CheckUpItemsProps} from './fields/checkupItems'

export function calculateAge(birthDate: Date) {
  const today = new Date()
  const age = today.getFullYear() - birthDate.getFullYear()
  const month = today.getMonth() - birthDate.getMonth()
  if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
    return age - 1
  }
  return age
}

const EMPTY_ARRAY: string[] = []
const PatientAddDialog = () => {
  const dividers = true
  const {onOpen: onNoticeDialogOpen} = useNoticeConfirm()
  const organizationType = storage.getOrganizationType()
  const uid = storage.getUserUid()
  const isHealthOrg = isOrganizationHealth(organizationType)
  const {onRefresh: onPatientListFetch} = usePatientSearch()
  const {t, i18n} = useTranslation()
  const classes = useStyles()
  const inputClasses = useBoldInputStyle()
  const {user: currentUser} = useAuth()
  const {userList: users} = useOrgUserList()
  const [docNo, setDocNo] = React.useState('')
  const [hasChartNo, setHasChartNo] = React.useState('')
  const [chartNo, setChartNo] = React.useState(false)
  const [usedEmail, setUsedEmail] = React.useState(false)
  const [changeBirth, setBirth] = React.useState<Date>()
  const [tabIndex, setTabIndex] = React.useState<number>(0)
  const contentRef = useRef<HTMLDivElement>(null)
  const {listAvailableLicenses} = useProductManagement()
  const [listCheckedPackage, setListCheckedPackage] = React.useState<string[]>(
    [],
  )

  const onUpdateCheckedPackage = useCallback<
    NonNullable<CheckUpItemsProps['onUpdateCheckedPackage']>
  >(
    (selectedItems) =>
      setListCheckedPackage(selectedItems.map((item) => String(item.itemId))),
    [],
  )

  const {
    open: isOpen,
    onClose: handleClose,
    onCreate,
    onGetProducts,
  } = useCreateDialog()
  const {idOver20Product} = useProductList()

  const AddSchema: SchemaOf<PatientAddForm> = yup
    .object()
    .shape({
      birth: yup.date().typeError(t('IDateInvalid')),
      chartNo: yup.string().required(t('IChartNoRequired')),
      countryCode: yup.string().required(t('ICountryRequired')),
      docNo: yup.string().required(t('IDoctorRequired')),
      gender: yup.string().required(t('IGenderRequired')),
      hand: yup.string().required(t('IHandednessRequired')),
      firstName: yup
        .string()
        .required(t('INameRequired'))
        .max(45, t('IMaxName', {max: 45})),
      lastName: yup
        .string()
        .required(t('INameRequired'))
        .max(20, t('IMaxName', {max: 20})),
      phone: yup
        .string()
        .required(t('ITelRequired'))
        .min(5)
        .matches(REGEX_PHONE, t('ITelInvalid')),
      email: yup.string().email(t('IEmailInvalid')).max(50, t('IEmailLength')),
      terms: yup.string().optional(),
      healthProductId: isHealthOrg
        ? yup.number().nullable().required(t('IMessageRequiredProduct'))
        : yup.number().nullable().notRequired(),
    })
    .defined()

  const {
    control,
    handleSubmit,
    formState: {errors},
    reset,
    setValue,
    setError,
    clearErrors,
  } = useForm<PatientAddForm>({
    // @ts-ignore
    resolver: yupResolver(AddSchema),
  })

  const isAdult = useMemo(() => {
    if (!changeBirth || !changeBirth.getMonth) {
      return false
    }
    const Age = calculateAge(changeBirth)
    return Number.isNaN(Age) ? false : Age >= 20
  }, [changeBirth])

  const handleTabChanged = (
    event: React.ChangeEvent<Record<string, unknown>>,
    newValue: number,
  ) => {
    setTabIndex(newValue)
  }

  const handleBirthData = (data: Date) => {
    setBirth(data)
  }

  const handleCleanClose = () => {
    setUsedEmail(false)
    setListCheckedPackage([])
    reset()
    handleClose()
    setHasChartNo('')
  }

  const handleChange = () => {
    setChartNo(!chartNo)
    setHasChartNo('')
  }

  const onSubmit = async (data: PatientAddForm) => {
    setHasChartNo('')
    const checkChartNo = await doubleCheckPatientChartNoApi(data.chartNo)
    if (checkChartNo.data) {
      setHasChartNo('IExistChartNo')
      if (contentRef.current) {
        contentRef.current.scrollTop = 0
      }
      return
    }
    if (!isAdult && idOver20Product.includes(String(data.healthProductId))) {
      setError('healthProductId', {
        type: 'manual',
        message: t('IErrorMessageAIDementiaPrediction'),
      })
      return
    }

    const {docNo} = data

    if (isNotDefined(docNo)) {
      console.log('의사를 선택해야 함')
      return
    }

    const doctor = users.find((user) => user.id === parseInt(docNo, 10))
    if (isNotDefined(doctor)) {
      console.log('의사를 선택하긴 했는데, 의사를 찾을 수 없음')
      return
    }

    try {
      const payload = {
        ...data,
        docNo: String(doctor.id),
        email: usedEmail ? '' : data.email,
        birth: dateToDashString(changeBirth ?? new Date()),
        healthProductId: [String(data.healthProductId), ...listCheckedPackage],
      }
      if (!listCheckedPackage.includes(NORM_PACKAGE_ID[0]))
        delete payload.zscore
      const result = await onCreate(payload)
      if (result && result.payload) {
        const codeResult = result.payload as any

        if (
          codeResult.message.includes('중복된') ||
          codeResult.message.includes('same')
        ) {
          setError('chartNo', {
            type: 'custom',
            message: `${t('IDuplicateChartNumber')}`,
          })
        }
      }
      handleCleanClose()
      onPatientListFetch()
    } catch (err) {
      console.log(err)
    }
  }

  const onCheckPackageByCls = (cls: string) => {
    const hasEEG = cls.includes('EEG')
    const hasHRV = cls.includes('HRV')
    const hasNormPackage = Array.from(
      new Set(listAvailableLicenses.map((license) => license.packageItemId)),
    ).some((packageItemId) => NORM_PACKAGE_ID.includes(packageItemId))
    setListCheckedPackage((prev) => {
      let filtered = [...prev]
      if (hasEEG && !hasHRV) {
        filtered = filtered.filter((id) => id !== HRV_INDICATOR_ID)
      }
      if (hasHRV && !hasEEG) {
        filtered = filtered.filter((id) => id !== EEG_INDICATOR_ID)
        if (hasNormPackage) {
          filtered = filtered.filter((id) => id !== NORM_PACKAGE_ID[0])
        }
      }
      return [
        ...filtered,
        ...(hasEEG && !filtered.includes(EEG_INDICATOR_ID)
          ? [EEG_INDICATOR_ID]
          : []),
        ...(hasHRV && !filtered.includes(HRV_INDICATOR_ID)
          ? [HRV_INDICATOR_ID]
          : []),
        ...(hasNormPackage && !filtered.includes(NORM_PACKAGE_ID[0])
          ? hasEEG
            ? [NORM_PACKAGE_ID[0]]
            : []
          : []),
      ]
    })
  }

  const onCheckPackage = (packageCls: string, packageItemId: string) => {
    setListCheckedPackage((prev) =>
      prev.includes(packageItemId)
        ? prev.filter((id) => id !== packageItemId)
        : [
            ...prev,
            packageItemId,
            ...(packageCls.includes('EEG') &&
            !prev.includes(EEG_INDICATOR_ID) &&
            packageItemId !== EEG_INDICATOR_ID
              ? [EEG_INDICATOR_ID]
              : []),
          ],
    )
  }

  const fetchChartNo = async () => {
    try {
      const response = await createPatientChartNoApi(currentUser?.uid ?? '')
      setValue('chartNo', response.data)
      clearErrors('chartNo')
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (chartNo) {
      fetchChartNo()
    }
  }, [chartNo])

  // useEffect(() => {
  //   const features = JSON.parse(storage.getFeatures() ?? '')
  //   if (features.includes(Features.PATIENT_USING_VISIT)) {
  //     setIsCheckedEEG(true)
  //     if (
  //       Array.from(
  //         new Set(
  //           listAvailableLicenses.map((license) => license.packageItemId),
  //         ),
  //       ).some((packageItemId) => NORM_PACKAGE_ID.includes(packageItemId))
  //     ) {
  //       setIsEnableNormDB(true)
  //     }
  //   }
  // }, [])

  useEffect(() => {
    return () => {
      setChartNo(false)
      setHasChartNo('')
      reset()
    }
  }, [isOpen])

  useEffect(() => {
    if (users) {
      const me: OrgSimpleUser | undefined = users.find(
        (item: OrgSimpleUser) => item.uid === uid,
      )
      if (me) {
        setValue('docNo', me.id?.toString())
        setDocNo(me.id?.toString())
      } else {
        setValue('docNo', String(users[0]?.id))
        setDocNo(String(users[0]?.id))
      }
    }
  }, [users])

  useEffect(() => {
    if (currentUser?.orgId) {
      onGetProducts()
    }
  }, [currentUser])

  return (
    <Dialog
      open={isOpen}
      onClose={handleCleanClose}
      className={classes.container}
    >
      <DialogContent
        dividers={dividers}
        ref={contentRef}
        style={{overflowY: 'auto'}}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.root}>
            {/* 상단 타이틀 컨테이너  */}
            <div className={classes.longTitleContainer}>
              <div className={classes.containerTitle}>
                <div className={classes.closeButtonWrap}>
                  <IconButton
                    color='secondary'
                    aria-label='favorite'
                    className={classes.closeButton}
                    onClick={() => {
                      onNoticeDialogOpen({
                        title: '',
                        message: t('ICancleConfirm'),
                      }).then((result) => {
                        if (result.payload) {
                          handleCleanClose()
                        }
                      })
                    }}
                  >
                    <CloseIcon className={classes.closeIcon} />
                  </IconButton>
                </div>
                <Typography variant='h6' className={classes.title}>
                  {t('ICustomerCreate')}
                </Typography>
              </div>
            </div>
            <Divider className={classes.titleDivider} />
            <DialogContent className={classes.contentWrap}>
              <Tabs
                value={tabIndex}
                indicatorColor='primary'
                textColor='primary'
                onChange={handleTabChanged}
                aria-label='disabled tabs example'
              >
                <Tab label={t('IBaseInfo')} />
                <Tab label={t('IDiseaseInfo')} />
              </Tabs>
              <TabPanel value={tabIndex} index={0}>
                <div className={classes.infoContainer}>
                  <Typography className={inputClasses.inputLabel}>
                    {t('IChartNo')}
                  </Typography>
                  <Typography className={classes.smallPadding} />
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Controller
                      name='chartNo'
                      key='chartNo'
                      control={control}
                      render={({field}) => (
                        <input
                          className={[
                            `${inputClasses.input}`,
                            `${errors.chartNo ? classes.error : ''}`,
                          ].join(' ')}
                          placeholder={t('IChartNo')}
                          {...field}
                          onChange={(e) => {
                            field.onChange(e)
                            setHasChartNo('')
                          }}
                          value={field.value ?? ''}
                        />
                      )}
                    />
                    <Typography className={classes.padding} />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={chartNo}
                          onChange={handleChange}
                          name={t('IChartNoCreate')}
                          color='primary'
                        />
                      }
                      label={
                        <Typography
                          style={{
                            color: '#5a6268',
                            fontSize: '17px',
                            paddingBottom: 3,
                          }}
                        >
                          {t('IChartNoCreate')}
                        </Typography>
                      }
                    />
                  </div>
                  <span className={classes.errorText}>
                    {errors.chartNo && t(errors.chartNo.message ?? '')}
                  </span>
                  <span className={classes.errorText}>
                    {t(hasChartNo ?? '')}
                  </span>
                </div>
                <div className={classes.infoContainer}>
                  <Typography className={inputClasses.inputLabel}>
                    {t('IName')}
                  </Typography>
                  <Typography className={classes.smallPadding} />
                  {(i18n.language === 'ko' || i18n.language === 'ja') && (
                    <div className={classes.clientName}>
                      <div className={classes.clientNameElm}>
                        <Controller
                          name='lastName'
                          key='lastName'
                          control={control}
                          render={({field}) => (
                            <input
                              className={[
                                `${inputClasses.input}`,
                                `${errors.lastName ? classes.error : ''}`,
                              ].join(' ')}
                              placeholder={t('ILastName')}
                              {...field}
                            />
                          )}
                        />
                        <Controller
                          name='firstName'
                          key='firstName'
                          control={control}
                          render={({field}) => (
                            <input
                              className={[
                                `${inputClasses.input}`,
                                `${errors.firstName ? classes.error : ''}`,
                              ].join(' ')}
                              placeholder={t('IFirstName')}
                              {...field}
                            />
                          )}
                        />
                      </div>
                      <div className={classes.clientNameElm}>
                        <span className={classes.errorName}>
                          {errors.lastName && t(errors.lastName.message ?? '')}
                        </span>
                        <span className={classes.errorName}>
                          {errors.firstName &&
                            t(errors.firstName.message ?? '')}
                        </span>
                      </div>
                    </div>
                  )}
                  {i18n.language !== 'ko' && i18n.language !== 'ja' && (
                    <div className={classes.clientName}>
                      <div className={classes.clientNameElm}>
                        <Controller
                          name='firstName'
                          key='firstName'
                          control={control}
                          render={({field}) => (
                            <input
                              className={[
                                `${inputClasses.input}`,
                                `${errors.firstName ? classes.error : ''}`,
                              ].join(' ')}
                              placeholder={t('IFirstName')}
                              {...field}
                            />
                          )}
                        />
                        <Controller
                          name='lastName'
                          key='lastName'
                          control={control}
                          render={({field}) => (
                            <input
                              className={[
                                `${inputClasses.input}`,
                                `${errors.lastName ? classes.error : ''}`,
                              ].join(' ')}
                              placeholder={t('ILastName')}
                              {...field}
                            />
                          )}
                        />
                      </div>
                      <div className={classes.clientNameElm}>
                        <div className={classes.errorName}>
                          {errors.firstName &&
                            t(errors.firstName.message ?? '')}
                        </div>
                        <div className={classes.errorName}>
                          {errors.lastName && t(errors.lastName.message ?? '')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className={classes.infoContainer}>
                  <Typography className={inputClasses.inputLabel}>
                    {t('ITel')}
                  </Typography>
                  <Typography className={classes.smallPadding} />
                  <div style={{display: 'flex', flexDirection: 'row', gap: 10}}>
                    <Controller
                      name='countryCode'
                      key='countrCode'
                      control={control}
                      defaultValue='KR'
                      render={({field}) => (
                        <SelectBoldCountryNumber {...field} />
                      )}
                    />
                    <Controller
                      name='phone'
                      key='phone'
                      control={control}
                      render={({field}) => (
                        <input
                          // error={!!errors.phone}
                          // helperText={errors.phone?.message}
                          placeholder={t('IPhone')}
                          className={[
                            `${inputClasses.input}`,
                            `${errors.phone ? classes.error : ''}`,
                          ].join(' ')}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <span className={classes.errorText}>
                    {errors.phone && t(errors.phone.message ?? '')}
                  </span>
                </div>
                <div className={classes.infoContainer}>
                  <Typography className={inputClasses.inputLabel}>
                    {t('IBirthday')}
                  </Typography>
                  <Typography className={classes.smallPadding} />
                  <SelectBoldYear
                    onDateChange={(e) => handleBirthData(e)}
                    oldDate={new Date()}
                  />
                </div>
                <div className={classes.infoContainer}>
                  <Typography className={inputClasses.inputLabel}>
                    {t('IGender')}
                  </Typography>
                  <Typography className={classes.smallPadding} />

                  <Controller
                    name='gender'
                    control={control}
                    defaultValue={Gender.MALE}
                    render={({field}) => (
                      <RadioGroup
                        row
                        className={classes.radioContainer}
                        {...field}
                      >
                        {GenderSelectItems.map((gender) => (
                          <FormControlLabel
                            control={<Radio color='primary' />}
                            key={gender.value}
                            label={
                              <p style={{marginRight: '30px'}}>
                                {t(gender.label)}
                              </p>
                            }
                            value={gender.value}
                          />
                        ))}
                      </RadioGroup>
                    )}
                  />
                </div>
                <div className={classes.infoContainer}>
                  <Typography className={inputClasses.inputLabel}>
                    {t('IFrequentlyUsedHands')}
                  </Typography>
                  <Typography className={classes.smallPadding} />

                  <Controller
                    name='hand'
                    control={control}
                    defaultValue={Hand.RIGHT}
                    render={({field}) => (
                      <RadioGroup
                        row
                        className={classes.radioContainer}
                        {...field}
                      >
                        {HandSelectItems.map((hand) => (
                          <FormControlLabel
                            control={<Radio color='primary' />}
                            key={hand.value}
                            label={
                              <p style={{marginRight: '30px'}}>
                                {t(hand.label)}
                              </p>
                            }
                            value={hand.value}
                          />
                        ))}
                      </RadioGroup>
                    )}
                  />
                </div>
                <div className={classes.infoContainer}>
                  <Typography className={inputClasses.inputLabel}>
                    {t('IEmail')}
                  </Typography>
                  <Typography className={classes.smallPadding} />
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <div className={classes.emailElm}>
                      <Controller
                        name='email'
                        key='email'
                        control={control}
                        render={({field}) => (
                          <input
                            className={inputClasses.input}
                            placeholder={t('IEmailRequired')}
                            disabled={usedEmail}
                            autoComplete='off'
                            // error={!!errors.chartNo}
                            // helperText={errors.chartNo?.message}
                            {...field}
                          />
                        )}
                      />
                    </div>
                    <Typography className={classes.padding} />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={usedEmail}
                          onChange={(e) => {
                            setUsedEmail(!usedEmail)
                            if (e.target.checked) {
                              setValue('email', '')
                            }
                          }}
                          name={t('IEmail')}
                          color='primary'
                        />
                      }
                      label={
                        <Typography
                          style={{
                            color: '#5a6268',
                            fontSize: '17px',
                            paddingBottom: 3,
                          }}
                        >
                          {t('INoEmail')}
                        </Typography>
                      }
                    />
                  </div>
                  {!usedEmail && (
                    <div className={classes.errorName}>
                      {errors.email && t(errors.email.message ?? '')}
                    </div>
                  )}
                </div>
                <div className={classes.infoContainer}>
                  <Typography className={inputClasses.inputLabel}>
                    {t('ICareDoctor')}
                  </Typography>
                  <Typography className={classes.smallPadding} />

                  <Controller
                    name='docNo'
                    key='docNo'
                    control={control}
                    defaultValue={docNo}
                    render={({field}) => (
                      <SelectBoldDoctor error={!!errors.docNo} {...field} />
                    )}
                  />
                  <span className={classes.errorText}>
                    {errors.docNo && t(errors.docNo.message ?? '')}
                  </span>
                </div>
                {isHealthOrg && (
                  <CheckUpItems
                    control={control}
                    errors={errors}
                    initialSelectedPackageItemIds={EMPTY_ARRAY}
                    onUpdateCheckedPackage={onUpdateCheckedPackage}
                  />
                )}
                {/* <Controller
                name='terms'
                control={control}
                defaultValue='https://isyncme.s3.ap-northeast-2.amazonaws.com'
                render={({field}) => <TextField type='hidden' {...field} />}
              /> */}
                {/* 고객 동의 활용  */}
                {/* <p className={classes.privacyText}>
                * {t('IAgreeBeforeRegister')}
              </p>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Button className={classes.privacyButton}>
                  {t('IPrivacyUsage')}
                </Button>
              </div> */}
              </TabPanel>
              <TabPanel value={tabIndex} index={1}>
                {t('INotYetImplemented')}
              </TabPanel>
              <Typography className={classes.smallPadding} />
            </DialogContent>
            {/* 하단 버튼 컨테이너  */}
            <Typography className={classes.smallPadding} />
            <div className={classes.buttonContainerBgColor}>
              <div className={classes.buttonContainer}>
                <Button
                  variant='contained'
                  color='default'
                  disableElevation
                  onClick={() => {
                    onNoticeDialogOpen({
                      title: '',
                      message: t('ICancleConfirm'),
                    }).then((result) => {
                      if (result.payload) {
                        handleCleanClose()
                      }
                    })
                  }}
                  className={classes.okButton}
                >
                  {t('ICancel')}
                </Button>
                <Typography className={classes.smallPadding} />
                <Button
                  variant='contained'
                  color='primary'
                  disableElevation
                  className={classes.okButtonColor}
                  type='submit'
                >
                  {t('IOk')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default PatientAddDialog
